import { useRef, useState, useCallback, useEffect } from "react";
import SearchBar from "./SearchBar.js";
import RecipientFilter from "../../Reusable/RecipientFilter.tsx";
import UserNavigatorMenu from "../../../../Reusable/UserNavigatorMenu/UserNavigatorMenu.js";
import useMediaQueries from "hooks/useMediaQueries.js";
import AnimationContainer from "components/Reusable/AnimationContainer/AnimationContainer.js";
import InboxSideBar from "../../SideBar/InboxSideBar.js";
import Icon, { IconWithTooltip } from "components/Elements/Icon/Icon.tsx";
import {
  searchInObj,
  filterInObj,
  getAvailablePickerProjectsAmount,
} from "../../helpers.js";
import loader from "helpers/loaderUrl.js";
import Button from "../../../../Elements/Button/Button.js";
import SelectedSubUserData from "../Toolbar/components/SelectedSubUserData/index.tsx";
import hasAdminAccessRights from "../../../../../helpers/hasAdminAccessRights.ts";
import CustomPopup from "../../Popups/CustomPopup.js";
import useOnClickOutside from "hooks/useOnClickOutside.js";
import usePickerQueryParamActions from "../../Picker/usePickerQueryParamActions.ts";
import useInbox from "hooks/useInbox.ts";
import { LinkToNewTab } from "../../../../Elements/Link/Link.js";
import { useContent } from "contexts/contentContext/contentContext.js";
import { useAppSelector } from "../../../../../store/hooks.ts";
import { usePicker } from "contexts/pickerContext/pickerContext.ts";
import { useUser } from "../../../../../contexts/userContext/userContext.js";
import {
  selectTParam,
  selectFParam,
  selectSearchParams,
} from "../../../../../store/selectors/inboxSelectors.ts";

const TabletSideBar = () => {
  const { tabletCondition } = useMediaQueries();
  const [showSideBar, setShowSideBar] = useState(false);

  return tabletCondition ? (
    <>
      <div className="main-part">
        <AnimationContainer
          animationOn={showSideBar}
          animationClassName="fade-in"
        >
          <div
            className={`sidebar-wrapper ${!showSideBar ? "transparent" : ""}`}
          />
        </AnimationContainer>
        <AnimationContainer
          animationOn={showSideBar}
          animationClassName="inbox-sidebar-animation"
        >
          <InboxSideBar setShowSideBar={setShowSideBar} />
        </AnimationContainer>
      </div>
      <div
        className="sidebar-icon"
        onClick={() => setShowSideBar((prev) => !prev)}
      >
        <Icon name="newInbox/arrows" />
      </div>
    </>
  ) : null;
};

const SplitAction = () => {
  const [openPopup, setOpenPopup] = useState(false);
  const { setSplitView, getSplitView } = useInbox();
  const { getTextByKey } = useContent();

  const ref = useRef(null);
  useOnClickOutside(ref, () => setOpenPopup(false));

  return (
    <div className="split-action" ref={ref}>
      <button
        className={`toggle-split-view ${
          getSplitView() ? "opened-split-view" : ""
        }`}
        onClick={() => {
          setSplitView(!getSplitView());
        }}
        aria-label={getTextByKey(
          getSplitView() ? "hide-split-view" : "show-split-view"
        )}
      >
        <IconWithTooltip
          iconProps={{
            name: getSplitView()
              ? "newInbox/Preview list/no_split"
              : "newInbox/Preview list/split",
            darkGray: true,
          }}
          tooltipProps={{
            tooltipTextKey: getSplitView()
              ? "hide-split-view"
              : "show-split-view",
          }}
        />
      </button>
      <Icon
        name="arrow-down-solid"
        gray
        onClick={() => {
          setOpenPopup((prev) => !prev);
        }}
      />
      <CustomPopup showState={openPopup} topPosition={38}>
        <div className="split-view-popup" onClick={() => setOpenPopup(false)}>
          <div
            className={`split-option${getSplitView() ? " selected" : ""}`}
            onClick={() => {
              setSplitView(true);
            }}
          >
            {getTextByKey("split-view")}
          </div>
          <div
            className={`split-option${!getSplitView() ? " selected" : ""}`}
            onClick={() => {
              setSplitView(false);
            }}
          >
            {getTextByKey("no-split-view")}
          </div>
        </div>
      </CustomPopup>
    </div>
  );
};

const DesktopToolBar = () => {
  const [loading, setLoading] = useState(false);

  const { desktopCondition, tabletCondition } = useMediaQueries();
  const { getMailsQuantity, getMailsCount, getSelectedList, fetchSubUserList } =
    useInbox();
  const pageType = useAppSelector(selectTParam);
  const pageCategory = useAppSelector(selectFParam);
  const searchParams = useAppSelector(selectSearchParams);
  const { getTextByKey } = useContent();
  const { getPickerMailList, getIsFetchingPickerData } = usePicker();
  const { isPremium, isPro, isBusiness, getUserData } = useUser();
  const availablePickerProjectsAmount = getAvailablePickerProjectsAmount(
    isPro(),
    isBusiness()
  );
  const { isLoggedIn } = useUser();
  const { openIframe } = usePickerQueryParamActions();

  useEffect(() => {
    if (getUserData()?.UserId) {
      fetchSubUserList();
    }
  }, [getUserData()?.UserId]);

  const handleCreatePicker = useCallback(async () => {
    setLoading(true);
    await openIframe("create");
    setLoading(false);
  }, [getUserData()]);

  const isManageStorage = window.location.pathname.includes("manageStorage");
  const { getSearchParams, getQueryParam } = useInbox();

  return desktopCondition || tabletCondition ? (
    <>
      <TabletSideBar />
      {searchParams?.q ? (
        <div className="target-title">
          {getTextByKey("search-in")} '{searchParams?.q}'
          <span className="target-subtitle">
            {pageType !== "picker" ? getMailsQuantity() : ""}{" "}
            {getTextByKey("search-results")}
          </span>
        </div>
      ) : (
        <div className="target-title">
          {pageCategory && !isManageStorage ? (
            <a href={`/l/en/inbox?t=${pageType}`}>
              {getTextByKey(searchInObj[pageType])}
            </a>
          ) : (
            <>
              {getTextByKey(
                isManageStorage ? "manage-storage" : searchInObj[pageType]
              )}
            </>
          )}
          {!!pageCategory && (
            <>
              <span className="target-subtitle">/</span>
              <span className="target-subtitle">
                {getTextByKey(filterInObj[pageCategory])}
                <span> ({getMailsCount()?.[pageCategory] || 0})</span>
              </span>
            </>
          )}
          {isManageStorage &&
          !getUserData()?.subUserRole &&
          getUserData()?.IsSubscribed ? (
            <LinkToNewTab page={"purchase/extra-storage"} isNewTab>
              <Button className="new-button">
                {getTextByKey("purchase-more")}
              </Button>
            </LinkToNewTab>
          ) : null}
          {pageType === "picker" && (
            <>
              <span className="target-subtitle">
                <span>
                  (
                  {`${getPickerMailList().length}${
                    availablePickerProjectsAmount
                      ? `/${availablePickerProjectsAmount}`
                      : ""
                  }`}
                  {!getIsFetchingPickerData() &&
                    !isPremium() &&
                    !getUserData()?.IsSubUser && (
                      <LinkToNewTab
                        page="plans"
                        classes="picker-upgrade-plan-link"
                        isNewTab
                      >
                        {getTextByKey("upgrade")}
                      </LinkToNewTab>
                    )}
                  )
                </span>
              </span>
            </>
          )}
        </div>
      )}
      <div>
        {!getSelectedList()?.length &&
          hasAdminAccessRights(
            getUserData()?.isTopUser,
            getUserData()?.subUserRole
          ) &&
          pageType !== "picker" && (
            <div className="recipient-filter-wrapper">
              <RecipientFilter />
            </div>
          )}
      </div>
      <SelectedSubUserData />
      {pageType === "picker" && (
        <Button
          className="create-picker-button"
          onClick={handleCreatePicker}
          disabled={loading}
        >
          <Icon
            src={loading ? loader : null}
            name={loading ? loader : "add"}
            white
          />
          {getTextByKey("create")}
        </Button>
      )}
      <div className="flex-spacer"></div>
      <SearchBar />
      {pageType !== "picker" && desktopCondition && <SplitAction />}
      {isLoggedIn() && desktopCondition ? (
        <UserNavigatorMenu isMinified />
      ) : (
        <div className="user-profile-spacer" />
      )}
    </>
  ) : null;
};
export default DesktopToolBar;
